import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import Drawer from '@mui/material/Drawer';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { Form } from 'components/billingAndUsage/components/Table/Form';
import ELEMENT from 'common/element';
import { BudgetCategoryType, FilterType, VolumesData } from 'components/billingAndUsage/types';
import {
  DELETE_BUDGET_CATEGORY,
  GET_BILLING_AND_USAGE_DATA,
  GET_VOLUMES,
  MOVE_BUDGET_CATEGORY,
} from 'components/billingAndUsage/queries';
import { CostManagementTable } from './CostManagementTable';

import './CostManagementTable.scss';

type CostManagementTableProps = {
  data: BudgetCategoryType[];
  volumesData: VolumesData;
};

export const CostManagementTableWrapper: FC<CostManagementTableProps> = ({
  data,
  volumesData,
}: CostManagementTableProps) => {
  const [moveBudgetCategory] = useMutation(MOVE_BUDGET_CATEGORY, {
    refetchQueries: [{ query: GET_BILLING_AND_USAGE_DATA }],
  });
  const [deleteBudgetCategory] = useMutation(DELETE_BUDGET_CATEGORY, {
    refetchQueries: [{ query: GET_BILLING_AND_USAGE_DATA }, { query: GET_VOLUMES }],
  });

  const filterInitialState: FilterType = {
    id: '',
    name: '',
    plannedGbPerDay: undefined, // make initial value undefined to show placeholder at the beggining
    logFilter: '',
  };
  const [currentFilter, setCurrentFilter] = useState<FilterType>(filterInitialState);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [drawerState, setDrawerState] = useState(false);

  const monthlyVolumes = volumesData?.categoryMonthlyVolumes ? volumesData?.categoryMonthlyVolumes : [];
  const dailyVolumes = volumesData?.categoryDailyVolumes ? volumesData?.categoryDailyVolumes : [];
  const otherMonthlyVolume = volumesData?.otherMonthlyVolumes ? volumesData?.otherMonthlyVolumes : null;
  const otherDailyVolume = volumesData?.otherDailyVolumes ? volumesData?.otherDailyVolumes : null;

  const handleDrawerOpen = (filter: FilterType): void => {
    setCurrentFilter(filter);
    setDrawerState(true);
  };

  const handleDrawerClose = (): void => {
    setDrawerState(false);
  };

  const handleShowConfirmDialog = (filter: FilterType): void => {
    setCurrentFilter(filter);
    setShowConfirmDialog(true);
  };

  const handleCloseConfirmDialog = (): void => {
    setShowConfirmDialog(false);
  };

  const handleDeleteCategory = (): void => {
    deleteBudgetCategory({ variables: { id: currentFilter.id } });
    setCurrentFilter(filterInitialState);
  };

  const handleMoveCategory = (vars: { id: number | string; position: number }): void => {
    moveBudgetCategory({ variables: vars });
  };

  return (
    <>
      <CostManagementTable
        monthlyVolumes={monthlyVolumes}
        dailyVolumes={dailyVolumes}
        otherMonthlyVolume={otherMonthlyVolume}
        otherDailyVolume={otherDailyVolume}
        budgetCategories={data}
        onShowConfirmDialog={handleShowConfirmDialog}
        onMove={handleMoveCategory}
        onDrawerOpen={handleDrawerOpen}
        testId={ELEMENT.planPageLogCategoryTable}
        testIdRow={ELEMENT.planPageCostManagementTableRow}
        testIdFooter={ELEMENT.planPageCostManagementTableFooter}
      />
      <Button
        variant={ButtonVariant.SECONDARY}
        onClick={(): void => handleDrawerOpen(filterInitialState)}
        className="CostManagementTable__Button"
        testId={ELEMENT.planPageAddNewCategoryButton}
      >
        Add New Category
      </Button>
      <Drawer anchor="right" open={drawerState} onClose={handleDrawerClose} className="CurrentBillingSettings__Drawer">
        <Form filter={currentFilter} onClose={handleDrawerClose} />
      </Drawer>
      <ConfirmDialog
        title="Confirm"
        text="Are you sure you want to delete this category? This cannot be undone."
        show={showConfirmDialog}
        close={handleCloseConfirmDialog}
        onSubmit={handleDeleteCategory}
      />
    </>
  );
};
